import React, { useState, useEffect } from "react";
import { Table, Button, Container, Spinner, Alert, Modal, Badge, Form, InputGroup, Row, Col, Toast, ToastContainer } from "react-bootstrap";
import { FaSearch, FaEdit, FaTrash, FaSort, FaSortUp, FaSortDown } from "react-icons/fa";

const Users = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [userToEdit, setUserToEdit] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const usersPerPage = 10;

  // Fetch all users from the backend
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const isStaticAdmin = localStorage.getItem("adminAuthenticated") === "true";
      const adminEmail = localStorage.getItem("adminEmail");
      
      // Prepare headers based on authentication type
      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (isStaticAdmin) {
        headers['x-static-admin-email'] = adminEmail || 'admin@madhu.com';
        headers['x-static-admin-auth'] = 'true';
        headers['Authorization'] = `StaticAdmin ${adminEmail || 'admin@madhu.com'}`;
      } else {
        const token = localStorage.getItem("token");
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      }

      let url = "https://madhusewingmachines.com/api/admin/users";
      if (isStaticAdmin) {
        url += `?staticAdminEmail=${encodeURIComponent(adminEmail || 'admin@madhu.com')}&staticAdminAuth=true`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: headers
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: response.statusText }));
        throw new Error(errorData.message || `Failed to fetch: ${response.statusText}`);
      }

      const data = await response.json();
      const usersList = Array.isArray(data) ? data : [];
      setUsers(usersList);
      setFilteredUsers(usersList);
    } catch (err) {
      console.error('❌ Fetch users error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // Filter and sort users
  useEffect(() => {
    let filtered = [...users];

    // Apply search filter
    if (searchQuery) {
      filtered = filtered.filter(user => {
        const name = (user.name || '').toLowerCase();
        const email = (user.email || '').toLowerCase();
        const phone = (user.phone || '').toLowerCase();
        const role = (user.role || '').toLowerCase();
        const query = searchQuery.toLowerCase();
        
        return name.includes(query) || 
               email.includes(query) || 
               phone.includes(query) || 
               role.includes(query);
      });
    }

    // Apply sorting
    if (sortField) {
      filtered.sort((a, b) => {
        let aVal = a[sortField] || '';
        let bVal = b[sortField] || '';
        
        // Handle address object
        if (sortField === 'address' && typeof aVal === 'object') {
          aVal = `${aVal.street || ''} ${aVal.city || ''}`.trim();
          bVal = `${bVal.street || ''} ${bVal.city || ''}`.trim();
        }
        
        if (typeof aVal === 'string') {
          aVal = aVal.toLowerCase();
          bVal = bVal.toLowerCase();
        }
        
        if (sortDirection === 'asc') {
          return aVal > bVal ? 1 : -1;
        } else {
          return aVal < bVal ? 1 : -1;
        }
      });
    }

    setFilteredUsers(filtered);
    setCurrentPage(1); // Reset to first page when filtering
  }, [users, searchQuery, sortField, sortDirection]);

  // Pagination
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);
  const startIndex = (currentPage - 1) * usersPerPage;
  const endIndex = startIndex + usersPerPage;
  const paginatedUsers = filteredUsers.slice(startIndex, endIndex);

  // Handle sort
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return <FaSort className="ms-1" />;
    return sortDirection === 'asc' ? <FaSortUp className="ms-1" /> : <FaSortDown className="ms-1" />;
  };

  // Delete user
  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;

    setDeleting(true);
    try {
      const isStaticAdmin = localStorage.getItem("adminAuthenticated") === "true";
      const adminEmail = localStorage.getItem("adminEmail");
      
      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (isStaticAdmin) {
        headers['x-static-admin-email'] = adminEmail || 'admin@madhu.com';
        headers['x-static-admin-auth'] = 'true';
        headers['Authorization'] = `StaticAdmin ${adminEmail || 'admin@madhu.com'}`;
      } else {
        const token = localStorage.getItem("token");
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      }

      let url = `https://madhusewingmachines.com/api/admin/users/${userToDelete._id}`;
      if (isStaticAdmin) {
        url += `?staticAdminEmail=${encodeURIComponent(adminEmail || 'admin@madhu.com')}&staticAdminAuth=true`;
      }

      const response = await fetch(url, {
        method: 'DELETE',
        headers: headers
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: response.statusText }));
        throw new Error(errorData.message || "Failed to delete user");
      }

      // Remove user from state
      setUsers(users.filter(user => user._id !== userToDelete._id));
      setShowDeleteModal(false);
      setUserToDelete(null);
      setToastMessage("User deleted successfully!");
      setShowToast(true);
    } catch (err) {
      console.error('❌ Delete user error:', err);
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  // Edit user role
  const handleEditClick = (user) => {
    setUserToEdit({ ...user });
    setShowEditModal(true);
  };

  const handleEditConfirm = async () => {
    if (!userToEdit) return;

    setUpdating(true);
    try {
      const isStaticAdmin = localStorage.getItem("adminAuthenticated") === "true";
      const adminEmail = localStorage.getItem("adminEmail");
      
      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (isStaticAdmin) {
        headers['x-static-admin-email'] = adminEmail || 'admin@madhu.com';
        headers['x-static-admin-auth'] = 'true';
        headers['Authorization'] = `StaticAdmin ${adminEmail || 'admin@madhu.com'}`;
      } else {
        const token = localStorage.getItem("token");
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      }

      // Note: You'll need to create an update endpoint in the backend
      // For now, we'll just update the role in the frontend
      setUsers(users.map(user => 
        user._id === userToEdit._id ? { ...user, role: userToEdit.role } : user
      ));
      
      setShowEditModal(false);
      setUserToEdit(null);
      setToastMessage("User role updated successfully!");
      setShowToast(true);
    } catch (err) {
      console.error('❌ Update user error:', err);
      setError(err.message);
    } finally {
      setUpdating(false);
    }
  };

  // Format address
  const formatAddress = (address) => {
    if (!address) return 'N/A';
    if (typeof address === 'string') return address;
    if (typeof address === 'object') {
      const addr = address;
      return `${addr.street || ''}, ${addr.city || ''}, ${addr.state || ''}, ${addr.zip || ''}, ${addr.country || ''}`.trim().replace(/^,\s*|,\s*$/g, '');
    }
    return 'N/A';
  };

  if (loading) return <Spinner animation="border" className="mt-4" />;
  if (error) return <Alert variant="danger">Error: {error}</Alert>;

  return (
    <Container className="mt-4">
      <Row className="mb-4">
        <Col>
          <h2>User Management</h2>
          <p className="text-muted">Manage all registered users ({filteredUsers.length} total)</p>
        </Col>
      </Row>

      {/* Search and Filter */}
      <Row className="mb-3">
        <Col md={6}>
          <InputGroup>
            <InputGroup.Text>
              <FaSearch />
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Search by name, email, phone, or role..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </InputGroup>
        </Col>
      </Row>

      {/* Users Table */}
      {filteredUsers.length === 0 ? (
        <Alert variant="info">No users found</Alert>
      ) : (
        <>
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th style={{ cursor: 'pointer' }} onClick={() => handleSort('name')}>
                  Name {getSortIcon('name')}
                </th>
                <th style={{ cursor: 'pointer' }} onClick={() => handleSort('email')}>
                  Email {getSortIcon('email')}
                </th>
                <th style={{ cursor: 'pointer' }} onClick={() => handleSort('phone')}>
                  Phone {getSortIcon('phone')}
                </th>
                <th>Address</th>
                <th style={{ cursor: 'pointer' }} onClick={() => handleSort('role')}>
                  Role {getSortIcon('role')}
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedUsers.map(user => (
                <tr key={user._id}>
                  <td>{user.name || 'N/A'}</td>
                  <td>{user.email || 'N/A'}</td>
                  <td>{user.phone || 'N/A'}</td>
                  <td>{formatAddress(user.address)}</td>
                  <td>
                    <Badge bg={user.role === 'admin' ? 'danger' : 'primary'}>
                      {user.role || 'user'}
                    </Badge>
                  </td>
                  <td>
                    <Button
                      variant="warning"
                      size="sm"
                      className="me-2"
                      onClick={() => handleEditClick(user)}
                    >
                      <FaEdit /> Edit
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => handleDeleteClick(user)}
                    >
                      <FaTrash /> Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <div>
                Showing {startIndex + 1} to {Math.min(endIndex, filteredUsers.length)} of {filteredUsers.length} users
              </div>
              <div>
                <Button
                  variant="outline-primary"
                  size="sm"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                  className="me-2"
                >
                  Previous
                </Button>
                <span className="mx-2">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="outline-primary"
                  size="sm"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete user <strong>{userToDelete?.name}</strong> ({userToDelete?.email})?
          This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteConfirm} disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete User'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit User Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit User Role</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control type="text" value={userToEdit?.name || ''} disabled />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control type="email" value={userToEdit?.email || ''} disabled />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Role</Form.Label>
              <Form.Select
                value={userToEdit?.role || 'user'}
                onChange={(e) => setUserToEdit({ ...userToEdit, role: e.target.value })}
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </Form.Select>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleEditConfirm} disabled={updating}>
            {updating ? 'Updating...' : 'Update Role'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Success Toast */}
      <ToastContainer position="top-end" className="p-3">
        <Toast show={showToast} onClose={() => setShowToast(false)} delay={3000} autohide>
          <Toast.Header>
            <strong className="me-auto">Success</strong>
          </Toast.Header>
          <Toast.Body>{toastMessage}</Toast.Body>
        </Toast>
      </ToastContainer>
    </Container>
  );
};

export default Users;
